import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import {
  RUSSIA_DRAWER_TABS,
  type RussiaDrawerTabId,
} from './components/RussiaRequestDrawerTabs.js';

/**
 * @aiPurpose sole writer/reader for the `?tab=` search param on the
 *   `/russia-rates/:id` drawer route. The drawer's open state itself
 *   is path-driven (`:id` is a route segment, not a query key), so
 *   unlike the china-side `useRequestUrlState` this hook owns only one
 *   key — `?tab=cargo|cost|chat` (#1548). Unknown / missing `?tab` values
 *   (including the now-removed `info`/`items`/`boxes`/`carriers`/`audit`)
 *   fall back to `cargo` WITHOUT rewriting the URL on render; the URL is
 *   rewritten only on an explicit `switchTab(...)` call, matching the
 *   china hook's pure read-side validation pattern.
 * @aiInvariant the canonical tab list lives in `RussiaRequestDrawerTabs`
 *   so the routing layer cannot drift from the rendering layer. Adding
 *   a tab requires updating that single constant.
 */
const DEFAULT_TAB: RussiaDrawerTabId = 'cargo';

export interface RussiaRequestUrlStateOpts {
  replace?: boolean;
}

function isValidTab(value: string | null): value is RussiaDrawerTabId {
  if (value === null) return false;
  return (RUSSIA_DRAWER_TABS as readonly string[]).includes(value);
}

export function useRussiaRequestUrlState(): {
  currentTab: RussiaDrawerTabId;
  switchTab: (tab: RussiaDrawerTabId, opts?: RussiaRequestUrlStateOpts) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const switchTab = useCallback(
    (tab: RussiaDrawerTabId, opts?: RussiaRequestUrlStateOpts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams],
  );

  const rawTab = searchParams.get('tab');
  const currentTab: RussiaDrawerTabId = isValidTab(rawTab) ? rawTab : DEFAULT_TAB;

  return { currentTab, switchTab };
}
